import { useAuth } from "@/context/AuthContext";
import { useAppConfig, useConfigStatus } from "@/context/ConfigContext";

// P0.3 — fundação do modo operador.
//
// O modo é APENAS apresentação: decide que navegação e que blocos de UI se
// mostram. As permissões continuam a ser validadas no servidor, que é a única
// autoridade (ver comentários em `lib/agenda.js`). Esconder um botão nunca
// substitui essa validação.
//
// Enquanto `operator_mode_enabled` for falso — ou enquanto a config ainda não
// tiver chegado — o modo é `default`, isto é, exactamente a UI actual para
// todos os perfis.

export const UI_MODE_DEFAULT = "default";
export const UI_MODE_OPERATOR = "operator";

export function isOperatorMode(user, config) {
  return user?.role === "operador" && config?.operator_mode_enabled === true;
}

export function uiModeFor(user, config) {
  return isOperatorMode(user, config) ? UI_MODE_OPERATOR : UI_MODE_DEFAULT;
}

// "Os meus indicadores" a partir de "O meu dia": a única porta do modo
// operador para o Dashboard (ver DashboardRoute).
export const INDICATORS_VIEW_PARAM = "indicadores";
export const OPERATOR_INDICATORS_PATH = `/dashboard?vista=${INDICATORS_VIEW_PARAM}`;

// Auditoria 2026-10 (decisão do cliente) — "O meu dia" é a casa de todos
// os operadores, com ou sem `operator_mode_enabled`. A flag continua a
// decidir só a navegação e os blocos do modo operador.
export function homePathFor(user, _config) {
  return user?.role === "operador" ? "/hoje" : "/dashboard";
}

// P0.6 — "ainda não sei qual é o modo".
//
// Quem só MOSTRA coisas trata a config em falta como modo default e não
// precisa disto. Quem ENCAMINHA precisa: mandar alguém para `/dashboard`
// antes de a config chegar dá um flash do Dashboard e, logo a seguir, um
// segundo salto para `/hoje`.
//
// Só o operador espera. Para gestor e admin o destino é `/dashboard` com
// flag ligada ou desligada, portanto decide-se já — o comportamento deles
// fica exactamente o de hoje, sem espera nenhuma.
export function isModePending(user, configStatus) {
  return user?.role === "operador" && configStatus === "loading";
}

export function useUiMode() {
  const { user } = useAuth();
  const config = useAppConfig();
  return uiModeFor(user, config);
}

export function useHomePath() {
  const { user } = useAuth();
  const config = useAppConfig();
  return homePathFor(user, config);
}

export function useModePending() {
  const { user } = useAuth();
  const configStatus = useConfigStatus();
  return isModePending(user, configStatus);
}

// Evolução, ranking e conquistas — três flags em escada, independentes de
// `operator_mode_enabled` e espelho exacto de `routes_recognition.effective_flags`.
//
//   performance → o operador vê o próprio score, componentes e comparação com
//                 a média da equipa ("A minha evolução").
//   ranking     → o operador vê o ranking completo dos classificados.
//   recognition → conquistas. Depende de `performance`, não de `ranking`.
//
// `ranking` e `recognition` exigem `performance`: gravadas soltas não mostram
// nada. A dependência é DERIVADA na leitura, nunca gravada — desligar
// `performance_enabled` fecha tudo sem perder o estado das outras duas, e
// voltar a ligá-la devolve a configuração anterior.
//
// Com as três desligadas (modo shadow) o motor continua a calcular e
// admin/gestor continuam a analisar em "Equipa → Adoção", que não depende
// destas flags. Esconder um bloco não substitui a validação no servidor, que
// devolve 403 e omite os dados.

export function isPerformanceEnabled(config) {
  return config?.performance_enabled === true;
}

export function isRankingEnabled(config) {
  return isPerformanceEnabled(config) && config?.ranking_enabled === true;
}

export function isRecognitionEnabled(config) {
  return isPerformanceEnabled(config) && config?.recognition_enabled === true;
}

export function effectiveFlags(config) {
  return {
    performance: isPerformanceEnabled(config),
    ranking: isRankingEnabled(config),
    recognition: isRecognitionEnabled(config),
  };
}

export function usePerformanceEnabled() {
  return isPerformanceEnabled(useAppConfig());
}

export function useRankingEnabled() {
  return isRankingEnabled(useAppConfig());
}

export function useRecognitionEnabled() {
  return isRecognitionEnabled(useAppConfig());
}
